// Address suggestions while writing mail: the address book from the archive (/api/contacts, best first), matched
// against the entry being typed in To, Cc or Bcc. Plain functions, so they can be tested without the DOM.

import { type Contact, api } from "./api";
import { fold } from "./format";

/** A contact with the words it is found by: of its name, its address and the parts of the address. */
export interface Suggestion extends Contact {
  words: string[];
}

export const MAX_SUGGESTIONS = 6;
/** How long a fetched address book is used before it is fetched again. */
const MAX_AGE_MS = 15 * 60 * 1000;

let book: Suggestion[] | null = null;
let fetchedAt = 0;
let pending: Promise<Suggestion[]> | null = null;

/** The address book, fetched when a message is started and used for MAX_AGE_MS. When it cannot be fetched (offline,
 * signed out) the one fetched before, or none: writing mail works without suggestions. */
export function loadContacts(): Promise<Suggestion[]> {
  if (book && Date.now() - fetchedAt < MAX_AGE_MS) return Promise.resolve(book);
  pending ??= api
    .contacts()
    .then(
      ({ contacts }) => {
        book = contacts.map(searchable);
        fetchedAt = Date.now();
        return book;
      },
      () => book ?? [],
    )
    .finally(() => {
      pending = null;
    });
  return pending;
}

/** After sending: the next message fetches the address book again, with the new recipients in it. */
export function forgetContacts(): void {
  fetchedAt = 0;
}

export function searchable(contact: Contact): Suggestion {
  const addr = fold(contact.addr);
  const [local, domain = ""] = addr.split("@");
  const words = [addr, local, domain, ...local.split(/[._+-]/), ...domain.split(/[.-]/), ...fold(contact.name ?? "").split(/[^\p{L}\p{N}]+/u)];
  return { ...contact, words: Array.from(new Set(words.filter(Boolean))) };
}

/** The contacts that match what is typed, in the address book's order: every typed word begins a word of the name or
 * the address ("anna h", "hansen", "anna@ex"). Addresses already in the message are left out. */
export function suggest(book: Suggestion[], typed: string, taken: Set<string>, limit = MAX_SUGGESTIONS): Suggestion[] {
  const wanted = fold(typed).split(/[\s<>"(),;]+/).filter(Boolean);
  if (wanted.length === 0) return [];
  const out: Suggestion[] = [];
  for (const c of book) {
    if (taken.has(c.addr) || !wanted.every((w) => c.words.some((word) => word.startsWith(w)))) continue;
    out.push(c);
    if (out.length === limit) break;
  }
  return out;
}

/** The entry of an address field the caret is in (entries are separated as in parseAddresses). */
export function entryAt(text: string, caret: number): { start: number; end: number; text: string } {
  const separator = /[,;\n]/;
  let start = Math.min(caret, text.length);
  let end = start;
  while (start > 0 && !separator.test(text[start - 1])) start--;
  while (end < text.length && !separator.test(text[end])) end++;
  return { start, end, text: text.slice(start, end).trim() };
}

/** A contact as an entry: `Name <addr>`, the name quoted when it holds characters that mean something in an address
 * header; characters that would split the entry are left out of it. */
export function formatContact(contact: Contact): string {
  const name = (contact.name ?? "").replace(/[",;<>\\\n]/g, " ").replace(/\s+/g, " ").trim();
  if (!name) return contact.addr;
  return /[()[\]:@.]/.test(name) ? `"${name}" <${contact.addr}>` : `${name} <${contact.addr}>`;
}

/** The field with the entry at the caret replaced by `entry`. At the end of the field a separator follows for the
 * next address; the caret goes after the entry. */
export function replaceEntry(text: string, caret: number, entry: string): { text: string; caret: number } {
  const at = entryAt(text, caret);
  const before = text.slice(0, at.start).replace(/[ \t]+$/, "");
  const after = text.slice(at.end);
  const head = before ? `${before} ${entry}` : entry;
  if (after.trim()) return { text: head + after, caret: head.length };
  return { text: `${head}, `, caret: head.length + 2 };
}
